/**
 * Minutes from what a person actually types into a duration field.
 *
 * A "Dauer in Minuten" number field is honest but slow: an hour and a quarter is
 * 75, and working that out is the user doing arithmetic the machine should do.
 * Every time tracker worth using (Toggl, Harvest, Clockify) accepts the shapes
 * people already think in, so this does too.
 *
 * The rules, in the order they are tried:
 *
 *   1:15        colon    -> hours:minutes            = 75
 *   1.25 / 1,25 decimal  -> HOURS                    = 75
 *   90          integer  -> MINUTES                  = 90
 *   2           small    -> HOURS                    = 120
 *   1h30 / 1h30m / 2 Std. 15 Min. / 45min  units     -> as written
 *
 * Two calls had to be made, and neither has a "correct" answer:
 *
 * A bare DECIMAL is hours, because the alternative is absurd: 1.25 minutes is 75
 * seconds, which nobody logs.
 *
 * A bare INTEGER is minutes - 90 has to mean 90 minutes, or it would mean 90
 * hours - EXCEPT below SMALL_INTEGER_IS_HOURS. Nobody books three minutes of
 * support by hand, but three hours is an ordinary afternoon, so a typed `3` means
 * hours. That is a product judgement about what this team actually logs, not a
 * property of the notation.
 *
 * It moves the seam rather than removing it: `4` is four hours and `5` is five
 * minutes, two adjacent keys 48x apart. There is no threshold without a seam, so
 * the field echoes its reading and spells the rules out beneath itself.
 *
 * German first: the decimal comma and "Std."/"Min." are what this admin's users
 * type, and an English-only parser would reject half of them.
 */

// Longest first, or "min" would match the "m" branch and leave "in" behind.
const HOUR_WORDS = 'stunden|stunde|stdn|std|hrs|hr|h';
const MINUTE_WORDS = 'minuten|minute|mins|min|m';

const NUMBER = '\\d+(?:\\.\\d+)?';

/**
 * Below this, a typed whole number is read as HOURS instead of minutes: nobody
 * books four minutes of support by hand, and four hours is half a day. Raising it
 * moves the seam up, it never removes it - see the note above.
 */
export const SMALL_INTEGER_IS_HOURS = 5;

/**
 * @param  {string|number|null} raw
 * @return {number|null} whole minutes, or null when the input means nothing
 */
export const parseDurationToMinutes = (raw) => {
    if (raw === null || raw === undefined) return null;

    const text = String(raw)
        .trim()
        .toLowerCase()
        // A German keyboard writes 1,25 — treat the comma as the decimal point it
        // is. `^` so a leading ",25" converts too, the way ".25" does.
        .replace(/(^|\d),(\d)/g, '$1.$2')
        .replace(/\s+/g, ' ');

    if (text === '') return null;

    // 1) 1:15 — the only shape where the second number is unambiguously minutes.
    //    Minutes above 59 are accepted rather than refused (1:75 -> 2:15): a typo
    //    is more useful shown back as "2:15 Std." than rejected with no reading.
    const colon = text.match(/^(\d+):(\d{1,2})\s*(?:h|std\.?|stunden?)?$/);
    if (colon) {
        return Math.round(Number(colon[1]) * 60 + Number(colon[2]));
    }

    // 2) explicit units, in any combination: 1h, 90m, 1h30, 1h30m, 2 Std. 15 Min.
    const hourMatch = text.match(new RegExp(`(${NUMBER})\\s*(?:${HOUR_WORDS})\\.?`));
    let rest = hourMatch ? text.replace(hourMatch[0], ' ') : text;
    const minuteMatch = rest.match(new RegExp(`(${NUMBER})\\s*(?:${MINUTE_WORDS})\\.?`));
    if (minuteMatch) rest = rest.replace(minuteMatch[0], ' ');

    if (hourMatch || minuteMatch) {
        // "1h30" — the trailing number carries no unit but can only be minutes.
        const bareRest = rest.trim().match(new RegExp(`^(${NUMBER})$`));
        if (rest.trim() !== '' && !bareRest) return null;

        const hours = hourMatch ? Number(hourMatch[1]) : 0;
        const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
        const trailing = bareRest ? Number(bareRest[1]) : 0;

        return Math.round(hours * 60 + minutes + trailing);
    }

    // 3) a bare number: decimal means hours, whole means minutes - unless it is
    //    small enough that minutes would be a nonsense booking.
    const bare = text.match(new RegExp(`^(${NUMBER})$`));
    if (bare) {
        const amount = Number(bare[1]);

        if (text.includes('.')) return Math.round(amount * 60);

        return amount > 0 && amount < SMALL_INTEGER_IS_HOURS
            ? Math.round(amount * 60)
            : Math.round(amount);
    }

    // 4) ".5" / ",5" — half an hour, written the way a spreadsheet would take it.
    const leadingDecimal = text.match(/^\.(\d+)$/);
    if (leadingDecimal) return Math.round(Number(`0.${leadingDecimal[1]}`) * 60);

    return null;
};

/**
 * The text form of a minute count that this parser reads back as the SAME
 * number. Formatting is not free: a bare integer below SMALL_INTEGER_IS_HOURS
 * comes back as HOURS (3 -> 180), so a three-minute session prefilled as `3`
 * read as "3:00 hrs" and a blur turned it into three billable hours.
 *
 * The colon form is the fix because it is the one shape with no seam and no
 * language in it: `0:03` is three minutes to this parser and to a reader.
 * Anything a bare number already round-trips is left as a bare number, so the
 * ordinary 45 and 90 keep looking the way people typed them.
 *
 * @param  {number|null} minutes
 * @return {string}
 */
export const formatMinutesForInput = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));

    if (total >= 60) {
        return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
    }

    // The unsafe window: 1-4 would be read back as hours.
    if (total > 0 && total < SMALL_INTEGER_IS_HOURS) {
        return `0:${String(total).padStart(2, '0')}`;
    }

    return String(total);
};

/**
 * The reading split into its parts. Deliberately NOT formatted here: the unit
 * words are language, and this file has no $t. The component that shows it does
 * the wording (see DurationInput) — the existing supportTimeFormat hardcodes
 * "Std."/"Min." and prints German into an English admin, which is the bug this
 * avoids repeating.
 *
 * @return {{hours: number, minutes: number, total: number}}
 */
export const splitDurationMinutes = (minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));

    return { hours: Math.floor(total / 60), minutes: total % 60, total };
};

/**
 * The seconds a running clock has actually counted: elapsed since it started,
 * minus the pauses that are over, minus the pause it may be in right now.
 * Unix seconds in, the way every date travels here; the stash and an entry
 * from the API both feed it (startedAt / started_at and so on).
 */
export const activeSeconds = (clock, nowMs = Date.now()) => {
    if (!clock) return 0;

    const now = Math.floor(nowMs / 1000);
    const startedAt = parseInt(clock.startedAt !== undefined ? clock.startedAt : clock.started_at, 10) || 0;
    const pausedAt = parseInt(clock.pausedAt !== undefined ? clock.pausedAt : clock.paused_at, 10) || 0;
    const pausedSeconds = parseInt(clock.pausedSeconds !== undefined ? clock.pausedSeconds : clock.paused_seconds, 10) || 0;
    const pausing = pausedAt ? Math.max(0, now - pausedAt) : 0;

    return Math.max(0, now - startedAt - pausedSeconds - pausing);
};

/** The pause a clock has spent so far, the one it is in included. */
export const pausedSecondsSoFar = (clock, nowMs = Date.now()) => {
    if (!clock) return 0;

    const now = Math.floor(nowMs / 1000);
    const pausedAt = parseInt(clock.pausedAt !== undefined ? clock.pausedAt : clock.paused_at, 10) || 0;
    const pausedSeconds = parseInt(clock.pausedSeconds !== undefined ? clock.pausedSeconds : clock.paused_seconds, 10) || 0;

    return pausedSeconds + (pausedAt ? Math.max(0, now - pausedAt) : 0);
};

/** h:mm:ss once there is an hour, mm:ss before, the way the header pill reads. */
export const formatClock = (seconds) => {
    const total = Math.max(0, parseInt(seconds, 10) || 0);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const pad = (value) => String(value).padStart(2, '0');

    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(total % 60)}` : `${pad(minutes)}:${pad(total % 60)}`;
};
